import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Transition } from "@headlessui/react";
import * as yup from "yup";
import { ValidationError } from "yup";
import PlacesAutocomplete from "./PlacesAutocomplete";
import TextInput from "./TextInput";
import RegionDropdown from "./RegionDropdown";
import { IShippingAddress } from "../types/types";
import { debounce } from "../utils/debounce";
import config from "../utils/config";
import {postcodeToRegion} from "../utils/postcodeToRegion";

interface IAddressInputProps {
  value: Partial<IShippingAddress> | null;
  onChange: (value: IShippingAddress | null) => void;
  onDraftChange?: (value: Partial<IShippingAddress> | null) => void;
  hideOptIn?: boolean;
  errors?: Record<string, string[]>;
}

export function isValidPhoneNumber(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }

  const phoneNumber = value.trim();
  if (!/^\+?[0-9\s().-]+$/.test(phoneNumber)) {
    return false;
  }

  if (phoneNumber.includes("+") && !phoneNumber.startsWith("+")) {
    return false;
  }

  const digits = phoneNumber.replace(/\D/g, "");
  const isInternational = phoneNumber.startsWith("+") || digits.startsWith("00");

  if (isInternational) {
    return digits.length >= 8 && digits.length <= 17;
  }

  return digits.length >= 6 && digits.length <= 12;
}

export const addressSchema = yup.object().shape({
  firstName: yup.string().required('Please enter your first name'),
  lastName: yup.string().required('Please enter your last name'),
  address1: yup.string().required('Please enter your address'),
  address2: yup.string().nullable(),
  city: yup.string().required('Please enter your city'),
  province: yup.string().required('Please select your region'),
  country: yup.string().required('Please enter your country'),
  zip: yup.string().required('Please enter your postcode'),
  phone: yup.string()
    .required('Please check your phone number is correct')
    .test('phone-number', 'Please check your phone number is correct', isValidPhoneNumber),
});

function hasCartUpdateAddressFields(address: Partial<IShippingAddress> | null): boolean {
  return Boolean(
    address?.address1?.trim() &&
    address?.city?.trim() &&
    address?.province?.trim() &&
    address?.country?.trim() &&
    address?.zip?.trim(),
  );
}

const AddressInput: React.FC<IAddressInputProps> = ({ value, onChange, onDraftChange, hideOptIn = false, errors = {} }) => {
  const [address, setAddress] = useState<Partial<IShippingAddress> | null>(
    value ? { ...value, country: config.countryCode } : value
  );
  const [showFullAddressForm, setShowFullAddressForm] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  const [isAutocompleteAvailable, setIsAutocompleteAvailable] = useState(true);
  const combinedErrors = useMemo(() => ({ ...errors, ...validationErrors }), [errors, validationErrors]);

  const validateForm = useCallback(() => {
    addressSchema.validate(address, { abortEarly: false })
      .then(() => setValidationErrors({}))
      .catch((validationError: ValidationError) => {
        const formErrors: Record<string, string[]> = {};
        if (validationError.inner) {
          validationError.inner.forEach((error) => {
            if (error.path) {
              formErrors[error.path] = [error.message];
            }
          });
        }
        setValidationErrors(formErrors);
      });
  }, [address]);

  useEffect(() => {
    if (value) {
      setShowFullAddressForm(true);
    }
  }, [value]);

  // Track if we need to send an update on blur
  const [pendingUpdate, setPendingUpdate] = useState(false);

  useEffect(() => {
    if (address) {
      setPendingUpdate(true);
    }
  }, [address]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      validateForm();
    }
  }, [errors, validateForm]);

  useEffect(() => {
    // Check if the PlacesAutocomplete service is available
    const checkAutocompleteAvailability = async () => {
      try {
        const isAvailable = await PlacesAutocomplete.checkAvailability();
        setIsAutocompleteAvailable(isAvailable);
      } catch (error) {
        console.error('Error checking Places API availability:', error);
        setIsAutocompleteAvailable(false);
      }
    };

    checkAutocompleteAvailability();
  }, []);

  debounce(validateForm, 300);

  const validateField = async (name: string, value: string) => {
    try {
      await addressSchema.validateAt(name, { [name]: value, country: config.countryCode });
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: [(error as yup.ValidationError).message]
        }));
      }
    }
  };

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    const updatedAddress = { ...address, [name]: value };
    setAddress(updatedAddress);
    onDraftChange?.(updatedAddress);
    validateField(name, value);
  };

  const handleFieldBlur = (event?: React.FocusEvent<HTMLInputElement>) => {
    const updatedAddress = { ...address };
    const fieldset = event?.currentTarget.closest('fieldset');

    fieldset?.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[name]').forEach((field) => {
      if (field.name !== 'country' && field.name !== 'sms_marketing_opt_in') {
        (updatedAddress as Record<string, string | null | undefined>)[field.name] = field.value;
      }
    });

    const addressChanged = JSON.stringify(updatedAddress) !== JSON.stringify(address);
    if (addressChanged) {
      setAddress(updatedAddress);
      onDraftChange?.(updatedAddress);
    }

    if (event && addressChanged) {
      validateField(event.target.name, event.target.value);
    }

    if ((pendingUpdate || addressChanged) && hasCartUpdateAddressFields(updatedAddress)) {
      onChange(updatedAddress as IShippingAddress);
      setPendingUpdate(false);
    }
  };

  const handleAddressAutocomplete = (value: Partial<IShippingAddress> | null) => {
    setValidationErrors({});
    if (!value) {
      setAddress({...address,
        address1: '',
        address2: null,
        city: '',
        province: '',
        zip: '',
        country: config.countryCode,
      });
      onDraftChange?.({
        ...address,
        address1: '',
        address2: null,
        city: '',
        province: '',
        zip: '',
        country: config.countryCode,
      });
    } else {
      if (value?.zip) {
        const region = postcodeToRegion(value.zip, config.countryCode);
        value.province = region.code;
      }
      const updatedAddress = {...address, ...value};
      setAddress(updatedAddress);
      onDraftChange?.(updatedAddress);
      setShowFullAddressForm(true);
    }
  };

  return (
    <fieldset>
      <div className="relative flex gap-3 mt-2">
        <div className="w-full">
          <TextInput
            type="text"
            name="firstName"
            label="First name"
            autoComplete="given-name"
            placeholder="First name"
            required={true}
            value={address?.firstName ?? ''}
            errors={combinedErrors}
            onChange={handleAddressChange}
            onBlur={handleFieldBlur}
          />
        </div>
        <div className="w-full">
          <TextInput
            type="text"
            name="lastName"
            label="Last name"
            autoComplete="family-name"
            placeholder="Last name"
            required={true}
            value={address?.lastName ?? ''}
            errors={combinedErrors}
            onChange={handleAddressChange}
            onBlur={handleFieldBlur}
          />
        </div>
      </div>
      <div className="mt-2">
        <div>
          {isAutocompleteAvailable ? (
            <PlacesAutocomplete
              name="address"
              initialValue={address?.address1 ?? ''}
              onChange={handleAddressAutocomplete}
              onBlur={handleFieldBlur}
              errors={combinedErrors}
            />
          ) : (
            <TextInput
              type="text"
              name="address1"
              label="Address"
              autoComplete="address-line1"
              placeholder="Address"
              required={true}
              onChange={handleAddressChange}
              value={address?.address1 ?? ''}
              errors={combinedErrors}
              onBlur={handleFieldBlur}
            />
          )}
        </div>
        <div className="overflow-hidden">
          <Transition
            show={showFullAddressForm}
            enter="transition ease duration-500 transform"
            enterFrom="opacity-0 -translate-y-12"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease duration-300 transform"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-12"
          >
            <div>
              <TextInput
                type="text"
                name="address2"
                label="Apartment, suite, etc."
                autoComplete="address-line2"
                placeholder="Apartment, suite, etc."
                hideLabel={true}
                onChange={handleAddressChange}
                value={address?.address2 ?? ''}
                errors={combinedErrors}
                onBlur={handleFieldBlur}
              />
              <div className="flex gap-3 items-start">
                <div className="flex-col w-full">
                  <TextInput
                    type="text"
                    name="city"
                    label="City"
                    hideLabel={true}
                    autoComplete="address-level2"
                    placeholder="City"
                    required={true}
                    onChange={handleAddressChange}
                    value={address?.city ?? ''}
                    errors={combinedErrors}
                    onBlur={handleFieldBlur}
                  />
                </div>
                <div className="flex-col w-full">
                  <RegionDropdown
                    value={address?.province ?? ''}
                    onChange={handleAddressChange}
                    onBlur={handleFieldBlur}
                    regions={config.shippingRegions}
                    error={combinedErrors.province?.[0]}
                    required={true}
                  />
                </div>
                <div className="flex-col w-full">
                  <TextInput
                    type="text"
                    name="zip"
                    label="Postcode"
                    hideLabel={true}
                    autoComplete="postal-code"
                    placeholder="Postcode"
                    required={true}
                    onChange={handleAddressChange}
                    value={address?.zip ?? ''}
                    errors={combinedErrors}
                    onBlur={handleFieldBlur}
                  />
                </div>
              </div>
            </div>
          </Transition>
        </div>
        <div className="mt-2">
          <label htmlFor="country" className="sr-only block text-sm font-medium text-gray-700">
            Country
          </label>
          <select
            id="country"
            name="country"
            autoComplete="country-name"
            className="relative block p-3 rounded-full w-full border-0 bg-transparent text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10 focus:ring-4 focus:ring-inset focus:ring-gray-300 sm:text-sm sm:leading-6"
            value={config.countryCode}
            onChange={() => {}}
          >
            <option value={config.countryCode}>{config.countryLabel}</option>
          </select>
        </div>
      </div>
      <div className="mt-2">
        <TextInput
          type="text"
          name="phone"
          label="Phone number (required)"
          autoComplete="tel"
          placeholder="Phone number (required)"
          required={true}
          value={address?.phone ?? ''}
          onChange={handleAddressChange}
          errors={combinedErrors}
          onBlur={handleFieldBlur}
        />
        {!hideOptIn && (
          <div className="mt-2 relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="sms_marketing_opt_in"
                name="sms_marketing_opt_in"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-gray-600 focus:ring-gray-600"
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label htmlFor="sms_marketing_opt_in" className="font-normal text-gray-900">
                Text me with news and offers
              </label>
            </div>
          </div>
        )}
      </div>
    </fieldset>
  );
};

export default AddressInput;
